import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Components
import LoadingScreen from './components/LoadingScreen';
import Sidebar from './components/Sidebar';

// Pages
import Home from './pages/Home';
import Skills from './pages/Skills';
import Projects from './pages/Projects';
import Timeline from './pages/Timeline';
import Certificates from './pages/Certificates';
import Contact from './pages/Contact';

import './App.css';

/* --------------------------------------------------------------
   Mobile swipe navigation (silent, no UI)
   -------------------------------------------------------------- */
const SWIPE_ROUTES = ['/', '/skills', '/projects', '/timeline', '/certificates', '/contact'];

// In jagahon par shuru hui swipe page nahi badlegi (inka apna horizontal scroll / kaam hai)
const NO_SWIPE =
  'input, textarea, select, video, [role="dialog"], ' +
  '.cp-overlay, .pm-wrap, .tl-overlay, .tech-marquee, .cp-chips, ' +
  '.su-tabs, .pw-mobile-filters, .tl-mobile-filters, .tl-drawer-filters';

function useMobileSwipeNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const start = useRef({ x: 0, y: 0, blocked: true });

  useEffect(() => {
    const onStart = (e) => {
      if (window.innerWidth > 768 || e.touches.length !== 1) {
        start.current.blocked = true;
        return;
      }
      const t = e.touches[0];
      start.current = {
        x: t.clientX,
        y: t.clientY,
        blocked: !!(e.target.closest && e.target.closest(NO_SWIPE))
      };
    };

    const onEnd = (e) => {
      const s = start.current;
      if (s.blocked || window.innerWidth > 768) return;
      if (document.body.style.overflow === 'hidden') return; 

      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < 80 || Math.abs(dx) < Math.abs(dy) * 1.5) return; 

      const i = SWIPE_ROUTES.indexOf(pathname);
      if (i === -1) return;
      if (dx < 0 && i < SWIPE_ROUTES.length - 1) navigate(SWIPE_ROUTES[i + 1]);
      else if (dx > 0 && i > 0) navigate(SWIPE_ROUTES[i - 1]);
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [pathname, navigate]);
}

/* page badalne par hamesha upar se shuru */
function useScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}

/* --------------------------------------------------------------
   App content (Router ke andar)
   -------------------------------------------------------------- */
function AppContent({ theme, toggleTheme }) {
  useMobileSwipeNavigation();
  useScrollToTop();

  return (
    <div className={`app-container ${theme}`}>
      <Sidebar theme={theme} toggleTheme={toggleTheme} />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/skills" element={<Skills />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/timeline" element={<Timeline />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/* --------------------------------------------------------------
   Main App
   -------------------------------------------------------------- */
function App() {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('portfolioTheme') || 'dark';
    } catch {
      return 'dark';
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.className = theme;
    try {
      localStorage.setItem('portfolioTheme', theme);
    } catch {
      /* private mode: ignore */
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const handleLoadingComplete = useCallback(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <Router>
      <AppContent theme={theme} toggleTheme={toggleTheme} />
    </Router>
  );
}

export default App;